import { create } from 'zustand'
import { QualityTierOverview, WorkflowVersion, WorkflowDetails } from '../types'

interface WorkflowState {
  // Data
  overview: QualityTierOverview[]
  workflowHistory: Record<string, WorkflowVersion[]>
  workflowDetails: Record<string, WorkflowDetails>
  
  // UI State
  selectedTier: string
  loading: boolean
  uploading: boolean
  testing: boolean
  
  // Actions
  setOverview: (overview: QualityTierOverview[]) => void
  setWorkflowHistory: (tier: string, history: WorkflowVersion[]) => void
  setWorkflowDetails: (workflowId: string, details: WorkflowDetails) => void
  setSelectedTier: (tier: string) => void
  setLoading: (loading: boolean) => void
  setUploading: (uploading: boolean) => void
  setTesting: (testing: boolean) => void
  
  // Computed
  getActiveWorkflow: (tier: string) => WorkflowVersion | undefined
  getTierOverview: (tier: string) => QualityTierOverview | undefined
  getWorkflowById: (workflowId: string) => WorkflowVersion | undefined
  
  // Reset
  reset: () => void
}

export const useWorkflowStore = create<WorkflowState>((set, get) => ({
  // Initial state
  overview: [],
  workflowHistory: {},
  workflowDetails: {},
  selectedTier: 'low',
  loading: false,
  uploading: false,
  testing: false,

  // Actions
  setOverview: (overview) => set({ overview }),
  
  setWorkflowHistory: (tier, history) => set((state) => ({
    workflowHistory: {
      ...state.workflowHistory,
      [tier]: history
    }
  })),
  
  setWorkflowDetails: (workflowId, details) => set((state) => ({
    workflowDetails: {
      ...state.workflowDetails,
      [workflowId]: details
    }
  })),
  
  setSelectedTier: (tier) => set({ selectedTier: tier }),
  setLoading: (loading) => set({ loading }),
  setUploading: (uploading) => set({ uploading }),
  setTesting: (testing) => set({ testing }),

  // Computed getters
  getActiveWorkflow: (tier) => {
    const state = get()
    const tierOverview = state.overview.find(t => t.quality_tier === tier)
    return tierOverview?.active_workflow
  },
  
  getTierOverview: (tier) => {
    const state = get()
    return state.overview.find(t => t.quality_tier === tier)
  },
  
  getWorkflowById: (workflowId) => {
    const state = get()
    // Search through all workflow history
    for (const history of Object.values(state.workflowHistory)) {
      const workflow = history.find(w => w.id === workflowId)
      if (workflow) return workflow
    }
    return undefined
  },

  // Reset
  reset: () => set({
    overview: [],
    workflowHistory: {},
    workflowDetails: {},
    selectedTier: 'low',
    loading: false,
    uploading: false,
    testing: false
  })
}))